import { Plus } from "lucide-react";

import type { BriefMemory } from "../hooks/useBriefMemory";
import type { AssetFileRef } from "./upload-assets/types";

import { AssetThumb } from "./AssetThumb";

/**
 * Files in one brief group, deduped by `attachmentId`. Entries missing
 * `attachmentId` or `url` are skipped — `assetFileSchema` rejects them, so the
 * brief should never hold one, and a tile could not render it anyway.
 */
function collectFiles(group: BriefMemory["assets"]): AssetFileRef[] {
  const seen = new Set<number>();
  const out: AssetFileRef[] = [];

  for (const entry of group?.files ?? []) {
    const file = entry as Record<string, unknown>;

    if (
      typeof file.attachmentId !== "number" ||
      typeof file.url !== "string" ||
      seen.has(file.attachmentId)
    ) {
      continue;
    }
    seen.add(file.attachmentId);

    out.push({
      attachmentId: file.attachmentId,
      mime: typeof file.mime === "string" ? file.mime : null,
      name: typeof file.name === "string" ? file.name : "file",
      size: typeof file.size === "number" ? file.size : null,
      url: file.url,
    });
  }

  return out;
}

function FileGroup({ files, title }: { files: AssetFileRef[]; title: string }) {
  return (
    <div className="mt-lg">
      <span className="text-sm font-semibold text-text-primary">{title}</span>
      {files.length > 0 ? (
        <div className="mt-md grid grid-cols-3 gap-sm">
          {files.map((file) => (
            <AssetThumb file={file} key={file.attachmentId} />
          ))}
        </div>
      ) : (
        <p className="mt-md text-xs text-text-tertiary">
          No {title.toLowerCase()} yet. Use “Add files” to upload.
        </p>
      )}
    </div>
  );
}

type UploadedFilesPanelProps = {
  memory: BriefMemory | null;
  /** Opens the upload modal (shared with the chat's attach button). */
  onAddAssets: () => void;
};

export function UploadedFilesPanel({
  memory,
  onAddAssets,
}: UploadedFilesPanelProps) {
  return (
    <aside className="w-full max-w-[320px] rounded-2xl border border-border-secondary bg-bg-primary p-xl shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-md font-semibold text-text-primary">
          Uploaded files
        </h2>
        <button
          className="flex items-center gap-1 text-sm font-semibold text-fg-brand-primary outline-hidden transition-colors hover:underline"
          onClick={onAddAssets}
          type="button"
        >
          <Plus className="size-4" />
          Add files
        </button>
      </div>

      <FileGroup files={collectFiles(memory?.assets)} title="Assets" />
      <FileGroup
        files={collectFiles(memory?.inspirations)}
        title="Inspirations"
      />
    </aside>
  );
}
